import {VerifiedUser} from "@mui/icons-material";
import {
	Alert,
	Button,
	Card,
	CardContent,
	CardHeader,
	Collapse,
	Divider,
	Grid,
	LinearProgress,
	Stack,
} from "@mui/material";
import {defined, ErrorAlert, RadioGroup, TextField, useFlag} from "@variocube/app-ui";
import React, {type FormEvent, useEffect, useState} from "react";
import {useAsync, useAsyncCallback} from "react-async-hook";
import {Invoice, useInvoicesApi} from "../../../api";
import {LabeledText} from "../../../component/labeled-text";
import {Typography} from "../../../component/typography";
import {useReloadInvoiceCount, useSetting, useUser} from "../../../context/auth";
import {useLocalization} from "../../../i18n";
import {notBlank} from "../../../util";
import {inSameInbox} from "./routing";

interface InvoiceAuditProperties {
	invoices: Invoice[];
	onAudited?: () => any;
}

function validateAndGetFirst(invoices: Invoice[]) {
	const [first] = invoices;
	if (!first || !inSameInbox(invoices)) {
		throw new Error("Invalid set of invoices for batch audit.");
	}
	return first;
}

export function isCarInvoice(invoice: Invoice) {
	return invoice.businessCases?.some(c => notBlank(c.licensePlate));
}

export function InvoiceAudit({
	invoices,
	onAudited,
}: Readonly<InvoiceAuditProperties>) {
	const {s, t} = useLocalization();
	const {approveInvoice, rejectInvoice, getIfNr} = useInvoicesApi();
	const user = useUser();
	const reloadInvoiceCount = useReloadInvoiceCount();

	const firstInvoice = validateAndGetFirst(invoices);

	const [progress, setProgress] = useState<number>();
	const [success, setSuccess] = useFlag(false);

	const rejectMinimumCharacters = useSetting("rejectMinimumCharacters") ?? 20;

	const showAuditCarIfnr = isCarInvoice(firstInvoice);
	const isAuditBySuperuser = firstInvoice.auditorUsers?.find(a => a.email === user.email) === undefined;
	const auditors = firstInvoice.auditorUsers
		?.map(a => {
			return `${a.firstname} ${a.lastname} (${a.email})`;
		})
		.join(", ");
	const defaultCarType = firstInvoice.businessCases?.find(c => notBlank(c.licensePlate))?.carType ?? "";

	const [ifnr, setIfnr] = useState("");
	const [carType, setCarType] = useState(defaultCarType);
	const [rejectReason, setRejectReason] = useState("");
	const [decision, setDecision] = useState<"Approve" | "Reject" | undefined>();

	const {result: fetchedIfnr} = useAsync(getIfNr, [firstInvoice.id]);
	useEffect(() => {
		if (fetchedIfnr) {
			setIfnr(fetchedIfnr);
		}
	}, [fetchedIfnr]);

	const {loading, error, execute} = useAsyncCallback(async (e: FormEvent) => {
		e.preventDefault();

		setProgress(0);

		for (let i = 0; i < invoices.length; i++) {
			const invoice = invoices[i];

			if (decision == "Approve") {
				await approveInvoice(invoice.id, {
					carType,
					carIfnr: ifnr,
				});
			}
			else if (decision == "Reject") {
				await rejectInvoice(invoice.id, {
					rejectReason,
				});
			}

			setProgress(100 * (i + 1) / invoices.length);
		}
		setProgress(100);
		setSuccess();
		if (onAudited) {
			onAudited();
		}
		await reloadInvoiceCount();
	});

	return (
		<Card
			component="form"
			onSubmit={execute}
		>
			<CardHeader
				title={<Typography startAdornment={<VerifiedUser />}>{t("invoices.details.audit")}</Typography>}
			/>
			<CardContent>
				{!success && (
					<Stack spacing={2}>
						{notBlank(firstInvoice.profitCenter) && (
							<LabeledText label={t("invoices.details.costCenter")} text={firstInvoice.profitCenter} />
						)}
						<RadioGroup
							value={decision}
							onChange={setDecision}
							options={["Approve", "Reject"]}
							renderLabel={s("invoices.details.auditDecisions")}
						/>
						<Collapse in={decision == "Approve" && showAuditCarIfnr}>
							<Stack spacing={2}>
								<TextField
									label="Interne Fahrzeug Nummer (IFNR)"
									fullWidth
									value={ifnr}
									onChange={setIfnr}
									disabled={loading}
								/>
								<TextField
									label={t("invoices.details.carTypeModel")}
									fullWidth
									value={carType}
									onChange={setCarType}
									disabled={loading}
									required={decision == "Approve" && showAuditCarIfnr}
								/>
							</Stack>
						</Collapse>
						<Collapse in={decision == "Reject"}>
							<TextField
								label={t("invoices.details.rejectReason", {minChar: rejectMinimumCharacters})}
								fullWidth
								multiline
								required={decision == "Reject"}
								rows={8}
								value={rejectReason}
								onChange={setRejectReason}
								disabled={loading}
								inputProps={{
									minLength: rejectMinimumCharacters,
								}}
							/>
						</Collapse>
						<Button
							fullWidth
							variant="contained"
							color="primary"
							disabled={loading}
							type="submit"
						>
							{t("actions.save")}
						</Button>

						{isAuditBySuperuser && (
							<>
								<Grid item xs={12}>
									<Divider />
								</Grid>
								<Grid item xs={12}>
									{/* eslint-disable react/no-danger */}
									<span
										dangerouslySetInnerHTML={{
											__html: t("invoices.details.superUserCanAuditHint", {auditors}),
										}}
									/>
								</Grid>
							</>
						)}

						{error && <ErrorAlert error={error} />}
					</Stack>
				)}
				{success && (
					<Alert severity="success">
						{decision === "Approve" && <Typography>{t("invoices.details.auditAcceptMessage")}</Typography>}
						{decision === "Reject" && <Typography>{t("invoices.details.auditRejectMessage")}</Typography>}
					</Alert>
				)}
			</CardContent>
			{defined(progress) && <LinearProgress variant="determinate" value={progress} color="secondary" />}
		</Card>
	);
}
